import React, { useState, useEffect, useMemo, useCallback, useContext, useRef } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import { secureGet, securePost } from "../../config/api";
import { formatDateTime } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { secureGetPdf } from "../../config/api";
import DeletePopup from "../../components/DeletePopup";
import GeneratePopup from "../../components/GeneratePopUp";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from "../../components/SnackbarNotification";
import Breadcrumbs from "../../components/Breadcrumbs";
import UserContext from "../../Shared/UserContext";

import downloadIcon from "../../icons/printer.svg";
import checkIcon from "../../icons/check.svg";
import chevronDownIcon from "../../icons/chevron-down.svg";
import chevronUpIcon from "../../icons/chevron-upG.svg";
import { PrimaryButton } from "../../components/PrimaryButton";
import AddIcon from "../../icons/plusB.svg";
import { DetailsHeaderVehicleRow } from "../../components/DetailsHeader";

const ORDER_STATUSES = [
  { key: "CREATED", label: "Créé", color: "#3872B0" },
  { key: "PARTIALLY_DELIVERED", label: "Partiellement livré", color: "#FEA26F" },
  { key: "DELIVERED", label: "Livré", color: "#06D6A0" },
];

const OrderDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const { t } = useTranslation();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { state: { role } } = useContext(UserContext);
  const dropdownRef = useRef(null);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    try {
      const response = await secureGet(`/carts/${id}`, showSnackbar, { signal });
      const fetchedOrder = response.data;
      setOrder(fetchedOrder);
      console.log("fetchedOrder", fetchedOrder);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch order:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [id, signal]);

  useEffect(() => {
    fetchOrder();

    return () => {
      abortController.abort();
    };
  }, [id, abortController, fetchOrder]);

  // Close status dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    };
    if (showStatusDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showStatusDropdown]);

  const handleUpdateStatus = async (status) => {
    setShowStatusDropdown(false);
    setUpdatingStatus(true);
    try {
      const payload = {
        cartId: order.id,
        status,
      };
      console.log("payload", payload);
      await securePost(`/carts/updateStatus`, payload, showSnackbar);
      showSnackbar(t(`confirmation.orderStatusUpdate`), "success");
      fetchOrder();
    } catch (error) {
      console.error("Error updating the order status:", error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading || !order) return <LoadingLogo />;

  const displayOrderStatus = order.status ?? ORDER_STATUSES[0].key;
  const currentOrderStatus = ORDER_STATUSES.find((s) => s.key === displayOrderStatus) || ORDER_STATUSES[0];

  const deliveryNotesList = Array.isArray(order.deliveryNotes)
    ? order.deliveryNotes
    : order.deliveryNote
      ? [order.deliveryNote]
      : [];

  const vehicleForDetailsHeader = order.vehicle
    ? {
        brand: order.vehicle?.brand?.name || order.vehicle?.brandMake || "—",
        model: order.vehicle?.model || "—",
        year: order.vehicle?.year || "—",
        type: order.vehicle?.type || "—",
        color: order.vehicle?.color || "—",
        plate: order.vehicle?.immatriculationNumber || "—",
        vin: order.vehicle?.vinNumber || "—",
      }
    : null;

  const canLinkClientProject =
    role === "ROLE_SERVICE_CONSULTANT" || role === "ROLE_WORKSHOP_MANAGER";
  const projectDetailsId = order.projectId ?? order.project?.id;

  const renderStatut = () => (
    <div className="flex items-center flex-wrap gap-1">
      <span className="px-[5px] py-1 rounded-[10px] font-semibold text-[12px] bg-[#0AD69F] text-white">
        BC
      </span>
      {deliveryNotesList.length > 0 ? (
        deliveryNotesList.map((dn, index) => (
          <React.Fragment key={dn.id ?? index}>
            <div className="w-2 h-[2px] bg-[#043F88]" />
            <span
              className={`px-[5px] py-1 rounded-[10px] font-semibold text-[12px] bg-[#0AD69F] text-white ${role === "ROLE_STOREKEEPER" ? "cursor-pointer" : ""}`}
              onClick={role === "ROLE_STOREKEEPER" && dn?.id ? () => history.push({ pathname: `/delivery-notes/details/${dn.id}` }) : undefined}
            >
              {deliveryNotesList.length === 1 ? "BL" : `BL${index + 1}`}
            </span>
          </React.Fragment>
        ))
      ) : (
        <>
          <div className="w-2 h-[2px] bg-[#043F88]" />
          <span className="px-[5px] py-1 rounded-[10px] font-semibold text-[12px] bg-[#EF476F] text-white">
            BL
          </span>
        </>
      )}
    </div>
  );

  return (
    <div>
      <Helmet>
        <title>Drivnest - {t("titles.order_details")}</title>
      </Helmet>
      {/* Header Section */}
      <div className="mb-1">
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-[#000000] ">{t(`titles.order_details`)}</h1>
        {/* State and Actions */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-[10px]">
            <p className="text-[#000000] font-semibold text-[24px]">
              {order.cartNumber}
            </p>
            <div className="flex space-x-1">{renderStatut()}</div>
            {role === "ROLE_STOREKEEPER" ? (

              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  disabled={updatingStatus}
                  className="px-4 py-2 bg-white text-[14px] font-bold rounded-[12px] flex items-center gap-2 hover:bg-gray-200 transition shadow-[0px_20px_80px_#00000011] disabled:opacity-50"
                  style={{ color: currentOrderStatus.color }}
                >
                  {currentOrderStatus.label}
                  <img
                    src={showStatusDropdown ? chevronUpIcon : chevronDownIcon}
                    alt="chevron"
                    className="w-4 h-4"
                  />
                </button>
                {showStatusDropdown && (
                  <div className="absolute top-full mt-1 right-0 w-[220px] px-2 bg-white rounded-[12px] py-2 z-10 shadow-[0px_20px_80px_#00000011]">
                    {ORDER_STATUSES.filter((s) => {
                      if (s.key === displayOrderStatus) return false;
                      if (s.key === "CREATED" && ["DELIVERED", "VALIDATED", "PARTIALLY_DELIVERED"].includes(displayOrderStatus)) return false;
                      return true;
                    }).map((status, index, array) => (
                      <React.Fragment key={status.key}>
                        <button
                          type="button"
                          onClick={() => handleUpdateStatus(status.key)}
                          className="w-full px-4 py-2 text-left text-[14px] font-semibold hover:bg-gray-100 transition"
                          style={{ color: status.color }}
                        >
                          {status.label}
                        </button>
                        {index < array.length - 1 && (
                          <div className="h-[1px] bg-[#F7F7F7]" />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <span
                className="px-1.5 py-1 rounded-[12px] font-semibold text-[14px] text-white"
                style={{ backgroundColor: currentOrderStatus.color }}
              >
                {currentOrderStatus.label}
              </span>
            )}
          </div>

          <div className="flex items-center">
            {role === "ROLE_STOREKEEPER" && order.status !== "DELIVERED" && (
              <PrimaryButton
                label={t("primaryButtons.add_deliveryNote") || "Créer un bon de livraison"}
                icon={AddIcon}
                onClick={() =>
                  history.push({
                    pathname: "/delivery-notes/add",
                    state: { fromOrder: order },
                  })
                }
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 text-[24px] text-primary font-semibold flex-wrap">
        {order.client?.code || order.client?.name ? (
          canLinkClientProject && order.client?.id ? (
            <Link to={`/clients/details/${order.client.id}`} className="underline">
              {order.client?.code ? `${order.client?.code} - ` : ""}
              {order.client?.name || ""}
            </Link>
          ) : (
            <span>
              {order.client?.code ? `${order.client?.code} - ` : ""}
              {order.client?.name || ""}
            </span>
          )
        ) : (
          <span className="text-gray-500">Client —</span>
        )}

        <span className="text-primary">-</span>

        {order.projectNumber ? (
          canLinkClientProject && projectDetailsId ? (
            <Link to={`/projects/details/${projectDetailsId}`} className="underline">
              {order.projectNumber}
            </Link>
          ) : (
            <span>{order.projectNumber}</span>
          )
        ) : (
          <span className="text-gray-500">—</span>
        )}

        <span className="text-primary">-</span>

        <span className="text-[#707070] font-semibold text-[24px]">
          {order.createdAt ? formatDateTime(order.createdAt) : "—"}
        </span>
      </div>

        <DetailsHeaderVehicleRow vehicle={vehicleForDetailsHeader} />


      {/* Product List */}
      <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">
        {t(`fields_types.list`)}
      </h3>
      <div className="grid grid-cols-2 gap-4">
        {order.details.map((product, index) => (
          <div
            key={index}
            className="grid grid-cols-[1fr_auto] items-center gap-4 bg-white px-2 py-2.5 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]"
          >
            <div className="ml-4">
              <p className="text-[#707070] text-[12px] font-semibold">{t("rows.designation")?.toUpperCase() ?? "DÉSIGNATION"}</p>
              <p className="text-primary font-semibold truncate">
                {product?.product?.name ?? product?.designation ?? ""}
              </p>
            </div>
            <div className="mr-4">
              <p className="text-[#707070] text-[12px] font-semibold">{t("rows.quantity")?.toUpperCase() ?? "QUANTITÉ"}</p>
              <p className="text-[#043F88] text-[20px] font-medium">
                {product?.quantity ?? 0}
              </p>
            </div>
          </div>
        ))}
      </div>


      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />

    </div>
  );
};

export default OrderDetails;

